import { ChatConfig, ModelCapabilities } from "src/reusable/Chat/types";
import { PersonaAPIData } from "../types";
import type { PersonaData } from "../stores/ChatStore";

export class PersonaConfigurationService {
  /**
   * Extract all model capabilities from persona's model version inference_data_config
   */
  static getModelCapabilities(persona: PersonaAPIData): ModelCapabilities {
    const inferenceConfig = persona.model_version?.inference_data_config;
    if (!inferenceConfig || typeof inferenceConfig !== "object") {
      return {};
    }

    // Only include capabilities that are explicitly defined in the config.
    // This prevents "not specified" from being treated as "explicitly false",
    // which would override existing capabilities (e.g., from the default model).
    const config = inferenceConfig as Record<string, unknown>;
    const capabilities: ModelCapabilities = {};
    if ("supports_vision" in config) {
      capabilities.supportsVision = config.supports_vision === true;
    }
    if ("supports_reasoning" in config) {
      capabilities.supportsReasoning = config.supports_reasoning === true;
    }
    if ("supports_function_calling" in config) {
      capabilities.supportsFunctionCalling =
        config.supports_function_calling === true;
    }
    return capabilities;
  }

  /**
   * Converts PersonaAPIData to ChatConfig
   * This centralizes the complex configuration building logic
   */
  static buildChatConfigFromPersona(
    persona: PersonaAPIData,
    fallbackConfig?: ChatConfig | null,
  ): ChatConfig {
    return {
      name: persona.name || "",
      slug: persona.slug || "",
      description: persona.description || "",
      initialPrompt: persona.initial_prompt || "",
      systemPrompt: persona.system_prompt || "",
      modelId: persona.config_data?.model_id ?? fallbackConfig?.modelId ?? null,
      modelVersionNum:
        persona.model_version?.version_number ??
        fallbackConfig?.modelVersionNum ??
        null,
      datasets: persona.datasets || [],
      chatControlMetadata: {},
      topSearchResults: persona.config_data?.top_search_results || 5,
      toolCalling: true,
      chatConfigId: persona.id,
      modelCapabilities: this.getModelCapabilities(persona),
    };
  }

  /**
   * Converts PersonaAPIData to PersonaData for store
   */
  static buildPersonaDataFromAPI(persona: PersonaAPIData): PersonaData {
    return {
      id: persona.id,
      slug: persona.slug,
      name: persona.name,
      description: persona.description || "",
      initialPrompt: persona.initial_prompt || "",
      systemPrompt: persona.system_prompt || "",
      datasets: persona.datasets || [],
      modelCapabilities: this.getModelCapabilities(persona),
    };
  }

  /**
   * Complete persona configuration update
   * Handles both ChatConfig and PersonaData updates
   */
  static configurePersona(
    persona: PersonaAPIData,
    fallbackConfig?: ChatConfig | null,
  ) {
    const chatConfig = this.buildChatConfigFromPersona(persona, fallbackConfig);
    const personaData = this.buildPersonaDataFromAPI(persona);

    return {
      chatConfig,
      personaData,
    };
  }
}

/** Seeded (system) agents are owned by the platform user (-1) and are
 *  labelled "Nuclearn" wherever an owner name is shown. */
export const SYSTEM_OWNER_USER_ID = -1;

export const ownerDisplayName = (
  persona: Pick<PersonaAPIData, "create_user_fk" | "user">,
): string =>
  persona.create_user_fk === SYSTEM_OWNER_USER_ID
    ? "Nuclearn"
    : persona.user?.user_display_name || "Unknown";

/** Which slice of the agent list to show; "all" applies no filter. */
export type AgentScope = "all" | "mine" | "shared" | "organization";

/**
 * Mine if the caller created it; otherwise Shared with me if it is private
 * (the list returns private agents the caller holds a grant on; an admin also
 * reads private agents bound to a workflow slot, which land here too), else
 * Public (the "organization" scope). A missing visibility_level counts as
 * public, as it was before agents could be private.
 */
export const agentScopeOf = (
  persona: Pick<PersonaAPIData, "create_user_fk" | "visibility_level">,
  userId: number | null | undefined,
): Exclude<AgentScope, "all"> => {
  if (userId != null && persona.create_user_fk === userId) return "mine";
  if (persona.visibility_level === "private") return "shared";
  return "organization";
};

export const filterByAgentScope = <
  T extends Pick<PersonaAPIData, "create_user_fk" | "visibility_level">,
>(
  personas: T[],
  scope: AgentScope,
  userId: number | null | undefined,
): T[] =>
  scope === "all"
    ? personas
    : personas.filter((persona) => agentScopeOf(persona, userId) === scope);

/** A tab on the agents page: an owner scope, or the agents the caller starred. */
export type AgentView = AgentScope | "starred";

export const filterByAgentView = <
  T extends Pick<
    PersonaAPIData,
    "create_user_fk" | "visibility_level" | "is_starred_by_user"
  >,
>(
  personas: T[],
  view: AgentView,
  userId: number | null | undefined,
): T[] =>
  view === "starred"
    ? personas.filter((persona) => persona.is_starred_by_user)
    : filterByAgentScope(personas, view, userId);

export type AgentSort = "stars" | "name" | "trending" | "owner" | "newest";

const byName = (a: { name?: string }, b: { name?: string }) =>
  (a.name ?? "").localeCompare(b.name ?? "", undefined, {
    sensitivity: "base",
  });

/**
 * A sorted copy. "stars": most stars first. "owner": by owner display name.
 * "newest": most recently created first. "trending": agents in `trendingIds`
 * in that order, then the rest by stars. Ties break by name.
 */
export const sortAgents = <
  T extends Pick<
    PersonaAPIData,
    "id" | "name" | "star_count" | "create_date" | "create_user_fk" | "user"
  >,
>(
  personas: T[],
  sort: AgentSort,
  trendingIds: number[] = [],
): T[] => {
  const byStars = (a: T, b: T) =>
    (b.star_count ?? 0) - (a.star_count ?? 0) || byName(a, b);
  if (sort === "name") return [...personas].sort(byName);
  if (sort === "stars") return [...personas].sort(byStars);
  if (sort === "owner")
    return [...personas].sort(
      (a, b) =>
        ownerDisplayName(a).localeCompare(ownerDisplayName(b), undefined, {
          sensitivity: "base",
        }) || byName(a, b),
    );
  if (sort === "newest")
    return [...personas].sort(
      (a, b) =>
        (b.create_date ?? "").localeCompare(a.create_date ?? "") ||
        byName(a, b),
    );
  const rank = new Map(trendingIds.map((id, index) => [id, index]));
  return [...personas].sort((a, b) => {
    const rankA = rank.get(a.id) ?? Infinity;
    const rankB = rank.get(b.id) ?? Infinity;
    if (rankA !== rankB) return rankA < rankB ? -1 : 1;
    return byStars(a, b);
  });
};
